import React, { useEffect, useState } from "react";
import { Intent } from "@blueprintjs/core";
import { Button, ButtonSave, TextButton } from "components/buttons";
import {
    FormHelperText,
    FormSlider,
    FormTextInput,
} from "components/form-fields";
import { Numeric } from "components/formatting";
import { Money } from "components/formatting/Money";
import { ShowHide } from "components/layout";
import { ListingTable } from "components/listing";
import {
    Modal,
    NotificationInline,
    NotificationToaster,
    SuccessPanel,
} from "components/notifications";
import { UserAccess, UserHasAccess } from "components/user-access";
import { PageRestricted } from "pages/errors/page-restricted";
import { useDispatch, useSelector } from "react-redux";
import { LicenceCheckingService } from "services";
import classNames from "classnames";

export function LicenceCheckingCreditsPurchase() {
    const licenceCheckingPurchaseAction = "LicenceChecking:PurchaseCredits";
    const defaultCreditAmount = 100;
    const pricelistHeaders = [
        "Credits",
        "Price Per Credit",
        "Discount (%)",
        "Available to Purchase Online",
    ];
    const hasAccess = UserHasAccess(licenceCheckingPurchaseAction);

    const memberNumber = useSelector(
        (state) => state.loggedInUser.memberNumber
    );
    const licenceCredits = useSelector(
        (state) => state.licenceChecking.licenceCredits
    );
    const greyFleetCredits = useSelector(
        (state) => state.licenceChecking.greyFleetCredits
    );

    const dispatch = useDispatch();
    const [isLoading, setIsLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [isSuccessful, setIsSuccessful] = useState(false);
    const [purchaseOrderNumber, setPurchaseOrderNumber] = useState("");
    const [licenceCheckingQuantity, setLicenceCheckingQuantity] =
        useState(defaultCreditAmount);
    const [greyFleetQuantity, setGreyFleetQuantity] =
        useState(defaultCreditAmount);
    const [priceList, setPriceList] = useState([]);
    const [isNonMember, setIsNonMember] = useState(true);
    const [errorWithPrice, setErrorWithPrice] = useState(false);
    const [licenceCheckingTotalCost, setLicenceCheckingTotalCost] = useState(0);
    const [greyFleetTotalCost, setGreyFleetTotalCost] = useState(0);
    const [minLicenceCheckPurchasePrice, setMinLicenceCheckPurchasePrice] =
        useState(0);
    const [minGreyFleetPurchasePrice, setMinGreyFleetPurchasePrice] =
        useState(0);
    const [showPriceListModal, setShowPriceListModal] = useState(false);
    const [priceListData, setPriceListData] = useState([]);

    useEffect(() => {
        getCredits();
    }, [hasAccess]);

    function getCredits() {
        if (!hasAccess) {
            return;
        }

        setIsLoading(true);

        const onApiFailure = function () {
            NotificationToaster.show(
                Intent.DANGER,
                "Unable to get credit prices, please try again."
            );
            setErrorWithPrice(true);
        };

        LicenceCheckingService.getCredits()
            .then((result) => {
                dispatch({
                    type: "SET_LICENCE_CREDITS",
                    payload: result.licenceCredits,
                });

                const sortedCreditPrices = result.creditPrices.sort(
                    (a, b) => a.minQuantity - b.minQuantity
                );
                setPriceList(sortedCreditPrices);
                setErrorWithPrice(sortedCreditPrices.length === 0);

                setIsNonMember(result.isNonMember);

                setPriceListData(
                    sortedCreditPrices.filter((item) => item.creditTypeKey === "LICENCECHECK").map((item) => {
                        return [
                            <span key={item.minQuantity}>
                                <Numeric>{item.minQuantity}</Numeric> -{" "}
                                <Numeric>{item.maxQuantity}</Numeric>
                            </span>,
                            <Money key={item.minQuantity}>{item.price}</Money>,
                            `${item.discountPercentage}%`,
                            item.canPurchaseOnline ? "Yes" : "No",
                        ];
                    })
                );
            }, onApiFailure)
            .catch(onApiFailure)
            .finally(() => {
                setIsLoading(false);
            });
    }

    function handlePurchaseCreditsSubmit() {
        setIsSaving(true);
        let order = {
            purchaseOrderNumber: purchaseOrderNumber,
            licenceCheckQuantity: licenceCheckingQuantity,
            greyFleetQuantity: greyFleetQuantity,
        };

        LicenceCheckingService.purchaseCredits(order)
            .then(
                () => {
                    setIsSuccessful(true);
                },
                (error) => {
                    const badRequestCode = 400;
                    const toastMessage =
                        error.response.status === badRequestCode
                            ? error.response.data
                            : "Unable to purchase credits, please try again.";

                    NotificationToaster.show(Intent.DANGER, toastMessage);
                }
            )
            .catch(() => {
                NotificationToaster.show(
                    Intent.DANGER,
                    "Unable to purchase credits, please try again."
                );
            })
            .finally(() => {
                setIsSaving(false);
            });
    }

    useEffect(
        function () {
            const licenceCheckPriceBand = priceList.find(
                (x) =>
                    x.minQuantity <= licenceCheckingQuantity &&
                    x.maxQuantity >= licenceCheckingQuantity && 
                    x.creditTypeKey === "LICENCECHECK"
            );

            const greyFleetPrice = priceList.find(
                (item) => item.creditTypeKey === "GREYFLEET"
            );

            if (!licenceCheckPriceBand && licenceCheckingQuantity > 0) {
                setErrorWithPrice(true);
                return;
            }

            if (!greyFleetPrice) {
                setErrorWithPrice(true);
                return;
            }

            const firstLicenceCheckPrice = priceList.find(
                (item) => item.creditTypeKey === "LICENCECHECK"
            );

            const licenceCheckingUnitCost =
                licenceCheckPriceBand?.price ?? firstLicenceCheckPrice.price;
            const greyFleetUnitCost = greyFleetPrice.price;

            setLicenceCheckingTotalCost(
                licenceCheckingUnitCost * licenceCheckingQuantity
            );
            setGreyFleetTotalCost(greyFleetUnitCost * greyFleetQuantity);

            setMinLicenceCheckPurchasePrice(
                firstLicenceCheckPrice ? firstLicenceCheckPrice.price : 0
            );
            setMinGreyFleetPurchasePrice(
                greyFleetPrice ? greyFleetPrice.price : 0
            );
        },
        [priceList, licenceCheckingQuantity, greyFleetQuantity]
    );

    function resetScreen() {
        setIsSuccessful(false);
        setPurchaseOrderNumber("");
        setLicenceCheckingQuantity(defaultCreditAmount);
        setGreyFleetQuantity(defaultCreditAmount);
    }

    function openModal() {
        setShowPriceListModal(true);
    }

    function closeModal() {
        setShowPriceListModal(false);
    }

    return (
        <UserAccess
            perform={licenceCheckingPurchaseAction}
            yes={() => (
                <div>
                    <ShowHide
                        evaluator={isSuccessful}
                        show={
                            <SuccessPanel>
                                <p className="spacer-bottom">
                                    Your Purchase Order has been submitted.
                                </p>
                                <Button
                                    intent="primary"
                                    text="Return to purchase screen"
                                    onClick={resetScreen}
                                ></Button>
                            </SuccessPanel>
                        }
                        hide={
                            <div>
                                <ShowHide
                                    evaluator={errorWithPrice}
                                    hide={
                                        <div className="spacer-bottom">
                                            <div className="spacer-bottom">
                                                <h3
                                                    className={classNames({
                                                        "bp6-skeleton":
                                                            isLoading,
                                                    })}
                                                >
                                                    Pricing
                                                </h3>
                                                <p
                                                    className={classNames({
                                                        "bp6-skeleton":
                                                            isLoading,
                                                    })}
                                                >
                                                    The pricing for the minimum
                                                    order of 10 Credits is{" "}
                                                    {
                                                        <Money>
                                                            {minLicenceCheckPurchasePrice *
                                                                10}
                                                        </Money>
                                                    }{" "}
                                                    for Licence Checking and{" "}
                                                    {
                                                        <Money>
                                                            {minGreyFleetPurchasePrice *
                                                                10}
                                                        </Money>
                                                    }{" "}
                                                    for Grey Fleet. Please use
                                                    the sliders below to select
                                                    the number of credits you
                                                    would like to purchase. A
                                                    discount is applied to
                                                    licence checking credits
                                                    depending on how many
                                                    credits you purchase. To
                                                    view a full price list for
                                                    licence checking{" "}
                                                    <TextButton
                                                        onClick={openModal}
                                                    >
                                                        click here
                                                    </TextButton>
                                                    .
                                                </p>
                                            </div>

                                            <NotificationInline
                                                intent={Intent.None}
                                                allowClose={false}
                                                text={
                                                    <>
                                                        <span className="bold">
                                                            Did you know?
                                                        </span>{" "}
                                                        Logistics UK Members
                                                        receive a discount on
                                                        Licence Checking
                                                        Credits. To find out
                                                        more or sign up click{" "}
                                                        <a
                                                            href="https://logistics.org.uk/membership/membership-enquiry"
                                                            target="_blank"
                                                            rel="noreferrer"
                                                        >
                                                            here
                                                        </a>
                                                        , or{" "}
                                                        <span className="bold">
                                                            call 03301 756133
                                                        </span>
                                                        .
                                                    </>
                                                }
                                                show={isNonMember}
                                            />
                                            <div className="spacer-bottom">
                                                <FormSlider
                                                    min={0}
                                                    max={1000}
                                                    stepSize={10}
                                                    labelStepSize={1000}
                                                    value={
                                                        licenceCheckingQuantity
                                                    }
                                                    onChange={(value) =>
                                                        setLicenceCheckingQuantity(
                                                            value
                                                        )
                                                    }
                                                    showLabels
                                                    leftLabelHeader="LICENCE CHECKING CREDITS"
                                                    leftLabelValue={
                                                        <Numeric>
                                                            {
                                                                licenceCheckingQuantity
                                                            }
                                                        </Numeric>
                                                    }
                                                    rightLabelHeader={
                                                        isNonMember
                                                            ? "TOTAL NON-MEMBER PRICE"
                                                            : "TOTAL PRICE"
                                                    }
                                                    rightLabelValue={
                                                        <span>
                                                            <Money>
                                                                {
                                                                    licenceCheckingTotalCost
                                                                }
                                                            </Money>
                                                        </span>
                                                    }
                                                    isSaving={isSaving}
                                                    isLoading={isLoading}
                                                />
                                            </div>

                                            <div className="spacer-bottom">
                                                <FormSlider
                                                    min={0}
                                                    max={1000}
                                                    stepSize={10}
                                                    labelStepSize={1000}
                                                    value={greyFleetQuantity}
                                                    onChange={(value) =>
                                                        setGreyFleetQuantity(
                                                            value
                                                        )
                                                    }
                                                    showLabels
                                                    leftLabelHeader="GREY FLEET CREDITS"
                                                    leftLabelValue={
                                                        <Numeric>
                                                            {greyFleetQuantity}
                                                        </Numeric>
                                                    }
                                                    rightLabelHeader={
                                                        isNonMember
                                                            ? "TOTAL NON-MEMBER PRICE"
                                                            : "TOTAL PRICE"
                                                    }
                                                    rightLabelValue={
                                                        <span>
                                                            <Money>
                                                                {
                                                                    greyFleetTotalCost
                                                                }
                                                            </Money>
                                                        </span>
                                                    }
                                                    isSaving={isSaving}
                                                    isLoading={isLoading}
                                                />
                                            </div>

                                            <NotificationInline
                                                intent={Intent.WARNING}
                                                show={
                                                    licenceCheckingQuantity +
                                                        licenceCredits <
                                                    greyFleetQuantity +
                                                        greyFleetCredits
                                                }
                                                text="For a Grey Fleet Check to complete, it will need to use a licence check credit as well if the employee being checked hasn't already had a licence check in the past 28 days. Please ensure you purchase enough licence check credits to enable your grey fleet checks to complete."
                                            />

                                            <ListingTable
                                                headers={[
                                                    "Credit Type",
                                                    "Quantity",
                                                    "Cost",
                                                ]}
                                                showPerPage={false}
                                                pageable={false}
                                                loadingData={false}
                                                tableClasses="pricing-table"
                                                data={[
                                                    [
                                                        "Licence checking credits",
                                                        <Numeric key="licence-checking-quantity">
                                                            {
                                                                licenceCheckingQuantity
                                                            }
                                                        </Numeric>,
                                                        <Money key="licence-checking-cost">
                                                            {
                                                                licenceCheckingTotalCost
                                                            }
                                                        </Money>,
                                                    ],
                                                    [
                                                        "Grey fleet credits",
                                                        <Numeric key="grey-fleet-quantity">
                                                            {greyFleetQuantity}
                                                        </Numeric>,
                                                        <Money key="grey-fleet-cost">
                                                            {greyFleetTotalCost}
                                                        </Money>,
                                                    ],
                                                    [
                                                        "Total",
                                                        "",
                                                        <Money key="licence-and-grey-fleet-total">
                                                            {licenceCheckingTotalCost +
                                                                greyFleetTotalCost}
                                                        </Money>,
                                                    ],
                                                ]}
                                                totalRecordCount={3}
                                            />
                                        </div>
                                    }
                                />

                                <h3
                                    className={classNames({
                                        "bp6-skeleton": isLoading,
                                    })}
                                >
                                    Buy with Purchase Order Number
                                </h3>
                                <ShowHide
                                    evaluator={errorWithPrice}
                                    show={
                                        <>
                                            <p
                                                className={classNames(
                                                    "spacer-bottom",
                                                    {
                                                        "bp6-skeleton":
                                                            isLoading,
                                                    }
                                                )}
                                            >
                                                Your account is not able to
                                                purchase credits by a Purchase
                                                Order at the moment. Please
                                                contact customer support on{" "}
                                                <span className="bold">
                                                    01892 552377
                                                </span>{" "}
                                                to resolve the issue.
                                            </p>
                                        </>
                                    }
                                    hide={
                                        <>
                                            <p
                                                className={classNames({
                                                    "bp6-skeleton": isLoading,
                                                })}
                                            >
                                                Purchase order number
                                            </p>
                                            <div className="inline-items spacer-bottom-small">
                                                <FormTextInput
                                                    placeholder="Please type your PO here..."
                                                    loading={isLoading}
                                                    value={purchaseOrderNumber}
                                                    onChange={(e) =>
                                                        setPurchaseOrderNumber(
                                                            e.target.value
                                                        )
                                                    }
                                                    large
                                                />
                                                <ButtonSave
                                                    intent="primary"
                                                    text="submit"
                                                    loading={isLoading}
                                                    onClick={
                                                        handlePurchaseCreditsSubmit
                                                    }
                                                    disabled={isSaving}
                                                    simpleDisabled={
                                                        isLoading ||
                                                        purchaseOrderNumber ===
                                                            "" ||
                                                        (licenceCheckingQuantity ===
                                                            0 &&
                                                            greyFleetQuantity ===
                                                                0)
                                                    }
                                                    icon={null}
                                                />
                                            </div>
                                            <div className="spacer-bottom">
                                                <FormHelperText
                                                    loading={isLoading}
                                                >
                                                    Once your PO has been
                                                    submitted please wait for
                                                    your credits to come through
                                                    while your order is
                                                    processed by our admin team.
                                                </FormHelperText>
                                            </div>
                                        </>
                                    }
                                />

                                <h3
                                    className={classNames({
                                        "bp6-skeleton": isLoading,
                                    })}
                                >
                                    Buy Over the Phone
                                </h3>
                                <p
                                    className={classNames({
                                        "bp6-skeleton": isLoading,
                                    })}
                                >
                                    To buy credits over the phone call{" "}
                                    <span className="bold">01892 552377</span>.
                                    Your Logistics UK Account Number is{" "}
                                    <span className="bold">{memberNumber}</span>
                                    , you will be asked to quote it during the
                                    call.
                                </p>
                            </div>
                        }
                    />
                    <Modal
                        isOpen={showPriceListModal}
                        title="Licence Checking Price List"
                        onClose={closeModal}
                    >
                        <ListingTable
                            headers={pricelistHeaders}
                            showPerPage={false}
                            pageable={false}
                            loadingData={false}
                            data={priceListData}
                            totalRecordCount={priceListData.length}
                            columnWidths={["auto", "auto", "auto", "160px"]}
                        />
                    </Modal>
                </div>
            )}
            no={() => <PageRestricted />}
        />
    );
}
